/**
 * "Weiter mit Facebook" entry points (2026-09-18).
 *
 * Web: the same server-side OAuth redirect flow as Google (/api/auth/facebook/start → facebook.com →
 * /api/auth/facebook/callback → /auth/callback#tokens). Shown ONLY inside the Facebook/Instagram/Messenger
 * in-app browsers — the one place Google refuses to sign in and Facebook signs in with one tap.
 *
 * Native: no Facebook SDK (its Android manifest drags the AD_ID permission in — Play blocked 1.0.12/vc13).
 * The app opens the same web flow in the SYSTEM browser (@capacitor/browser → Custom Tabs /
 * SFSafariViewController) with client=app and a PKCE challenge; the callback comes back on the custom scheme
 * snugtale://auth/callback?code=… and lib/authHandoff.ts redeems the code with the verifier. Needs the scheme
 * in the shell (Android intent filter + iOS URL type, first in 1.0.14) → shell gate.
 */
import { getPlatform, isNativeApp } from './platform';
import { inAppBrowser } from './inAppBrowser';
import { parentGateNeeded } from './ageGate';
import { pendingClaimCode } from './anonStory';
import { challengeFor, randomVerifier } from './pkce';
import { facebookButtonMode, FACEBOOK_MIN_SHELL as MIN_SHELL } from './facebookAuthCore';
import { getShellVersion } from './shellVersion';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const FACEBOOK_MIN_SHELL = MIN_SHELL;
const VERIFIER_KEY = 'snugtale_fb_verifier';
const OVERRIDE_KEY = 'snugtale_facebook_override';
const INTENT_KEY = 'snugtale_fb_intent';

export type FacebookAvailability = 'web' | 'native' | 'unavailable';

/** Synchronous part: web = only inside a social in-app browser (or any browser once the tester door is armed —
 *  Meta's reviewers test in a desktop browser); native = decided by the shell version (async). */
export async function getFacebookAvailability(): Promise<FacebookAvailability> {
  const native = isNativeApp();
  const mode = facebookButtonMode({
    enabled: true, native, inAppBrowser: inAppBrowser() !== null || (!native && facebookTesterOverride()),
    shellVersion: native ? await getShellVersion() : null, parentGateNeeded: native && parentGateNeeded(),
  });
  return mode ?? 'unavailable';
}

/** Tester door while the Meta app is not Live (FACEBOOK_LOGIN_PUBLIC=false → config `configured` without
 *  `enabled`): `?fbtest=1` on /login or /register arms the button for this browser, `?fbtest=0` clears it.
 *  Facebook itself only lets people with a role on the app through in development mode. */
export function facebookTesterOverride(): boolean {
  try {
    const v = new URLSearchParams(window.location.search).get('fbtest');
    if (v === '1') localStorage.setItem(OVERRIDE_KEY, '1');
    else if (v === '0') localStorage.removeItem(OVERRIDE_KEY);
    return localStorage.getItem(OVERRIDE_KEY) === '1';
  } catch {
    return false;
  }
}

export function getFacebookStartUrl(params: { next?: string; claimToken?: string | null; client?: 'web' | 'app'; codeChallenge?: string }): string {
  const q = new URLSearchParams({ app_id: 'snugtale', client: params.client ?? 'web' });
  if (params.next) q.set('next', params.next);
  if (params.claimToken) q.set('claim_token', params.claimToken);
  else { const code = pendingClaimCode(); if (code) q.set('claim_code', code); }
  if (params.codeChallenge) q.set('code_challenge', params.codeChallenge);
  // account_created's platform for the app flow (the callback runs in the system browser).
  if (params.client === 'app') { const pf = getPlatform(); if (pf !== 'web') q.set('platform', pf); }
  return `${API_URL}/auth/facebook/start?${q.toString()}`;
}

export function startWebFacebookLogin(params: { next?: string; claimToken?: string | null }): void {
  window.location.assign(getFacebookStartUrl({ ...params, client: 'web' }));
}

/** Persist the verifier + intent across the browser round-trip (the WebView may be recreated meanwhile). */
async function stash(key: string, value: string | null): Promise<void> {
  try {
    const { Preferences } = await import('@capacitor/preferences');
    if (value === null) await Preferences.remove({ key }); else await Preferences.set({ key, value });
    return;
  } catch { /* fall back to localStorage */ }
  try { if (value === null) localStorage.removeItem(key); else localStorage.setItem(key, value); } catch { /* ignore */ }
}
async function unstash(key: string): Promise<string | null> {
  try {
    const { Preferences } = await import('@capacitor/preferences');
    const { value } = await Preferences.get({ key });
    if (value) return value;
  } catch { /* fall back */ }
  try { return localStorage.getItem(key); } catch { return null; }
}

export async function startNativeFacebookLogin(params: { next?: string; claimToken?: string | null }): Promise<void> {
  const verifier = randomVerifier();
  const challenge = await challengeFor(verifier);
  await stash(VERIFIER_KEY, verifier);
  await stash(INTENT_KEY, JSON.stringify({ next: params.next ?? '/', claimToken: params.claimToken ?? null, at: Date.now() }));
  const url = getFacebookStartUrl({ ...params, client: 'app', codeChallenge: challenge });
  const { Browser } = await import('@capacitor/browser');
  await Browser.open({ url, presentationStyle: 'popover' });
}

export async function takeNativeFacebookIntent(): Promise<{ verifier: string; next: string; claimToken: string | null } | null> {
  const verifier = await unstash(VERIFIER_KEY);
  const rawIntent = await unstash(INTENT_KEY);
  await stash(VERIFIER_KEY, null);
  await stash(INTENT_KEY, null);
  if (!verifier) return null;
  let next = '/';
  let claimToken: string | null = null;
  try { const i = JSON.parse(rawIntent || '{}'); if (typeof i.next === 'string') next = i.next; if (typeof i.claimToken === 'string') claimToken = i.claimToken; } catch { /* ignore */ }
  return { verifier, next: next.startsWith('/') && !next.startsWith('//') ? next : '/', claimToken };
}
